import {
    EPreApprovalVerdicts,
    fetchPreapprovalVerdict,
} from '@/apiCalls/fetchPreapprovalVerdict';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { EButtonType } from '@/components/Button';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    ELoanUsage,
    EUserFlow,
    selectIsVehicleKnown,
    selectLoanUsage,
    selectRegistrationNumber,
    selectVehicleBuildYear,
    selectVehicleDescription,
    selectVehicleKilometres,
    selectVehicleMake,
    selectVehicleModel,
    selectVehicleRegistrationState,
    selectVehicleVariant,
    setIsPreapproved,
    setIsVehicleKnown,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectHideSellerScreen,
    selectPreviousDisplay,
    selectRequireGlassGuide,
    selectUserFlow,
    setDisplay,
    setModal,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
// eslint-disable-next-line no-restricted-imports

interface IVehicleButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId: string;
}

export function VehicleButtons({
    setShowErrors,
    baseId,
}: IVehicleButtonsProps): JSX.Element {
    // ***** CONSTANTS *****
    const vehicleDescription = useAppSelector(selectVehicleDescription);

    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const hideSellerScreen = useAppSelector(selectHideSellerScreen);
    const loanUsage = useAppSelector(selectLoanUsage);
    const previousDisplay = useAppSelector(selectPreviousDisplay);
    const isGlassRequired = useAppSelector(selectRequireGlassGuide);
    const userFlow = useAppSelector(selectUserFlow);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
    const buildYear = useAppSelector(selectVehicleBuildYear);
    const vehicleMake = useAppSelector(selectVehicleMake);
    const vehicleModel = useAppSelector(selectVehicleModel);
    const vehicleVariant = useAppSelector(selectVehicleVariant);

    const registrationNumber = useAppSelector(selectRegistrationNumber);
    const vehicleKiloMetres = useAppSelector(selectVehicleKilometres);
    const vehicleState = useAppSelector(selectVehicleRegistrationState);
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);

    // ***** Event Handlers *****
    async function handleNextClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        setShowErrors(false);
        if (
            previousDisplay === EDisplays.INDICATIVELY_APPROVED &&
            isNovatedLease
        ) {
            const nextScreen = await getNextScreen();
            dispatch(setDisplay(nextScreen));
            return;
        }
        await getQuickQuote();
        const state = store.getState();
        if (
            state.lenderReducer.lenders?.length === 0 &&
            loanUsage === ELoanUsage.NOVATED
        ) {
            if (!isLoggedIn) {
                dispatch(setModal(EModals.REGISTER));
            }
            dispatch(setDisplay(EDisplays.INELIGIBLE));
            return;
        }
        const nextScreen = await getNextScreen();
        dispatch(setDisplay(nextScreen));
    }

    function handleBackClick(): void {
        if (
            userFlow === EUserFlow.NO_LENDER ||
            userFlow === EUserFlow.NO_LENDER_EARLY_SIGNUP
        ) {
            dispatch(setDisplay(EDisplays.NO_LENDER_HOME));
            return;
        }
        if (isNovatedLease) {
            dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
            return;
        }
        dispatch(setDisplay(EDisplays.LOAN));
    }

    // ***** Helpers ******
    function isComplete(): boolean {
        if (isGlassRequired && !vehicleDescription) return false;
        if (isVehicleKnown) return true;
        if (
            (!buildYear || !vehicleMake || !vehicleModel || !vehicleVariant) &&
            (!registrationNumber || !vehicleKiloMetres || !vehicleState)
        )
            return false;
        dispatch(setIsVehicleKnown(true));
        return true;
    }

    async function getNextScreen(): Promise<EDisplays> {
        if (
            previousDisplay === EDisplays.INDICATIVELY_APPROVED &&
            isNovatedLease
        ) {
            try {
                const response = await fetchPreapprovalVerdict();
                if (response.approvedLenders?.length) {
                    dispatch(setIsPreapproved(true));
                    if (response.verdict === EPreApprovalVerdicts.APPROVED) {
                        return EDisplays.INDICATIVELY_APPROVED;
                    } else {
                        return EDisplays.INDICATIVELY_REFERRED;
                    }
                } else {
                    dispatch(setIsPreapproved(false));
                    return EDisplays.INDICATIVELY_REFERRED;
                }
            } catch (error) {
                return EDisplays.INDICATIVELY_REFERRED;
            }
        }
        if (previousDisplay === EDisplays.INDICATIVELY_APPROVED) {
            return EDisplays.PREPPROVAL_ANIMATION;
        }
        if (hideSellerScreen) {
            if (loanUsage === ELoanUsage.BUSINESS) {
                return EDisplays.BUSINESS;
            }
            return EDisplays.BASIC;
        }
        return EDisplays.SELLER;
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
            leftType={EButtonType.SECONDARY_OUTLINE}
        />
    );
}
